import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBan,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faXmark,
} from '@fortawesome/sharp-solid-svg-icons';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import React, { useEffect } from 'react';
import { useMountedState } from 'react-use';
import { SCComponentRoot } from '../component_root';
import './index.css';

interface SCToastProps {
  id: string;
  content: string | ReactNode;
  type?: 'info' | 'warning' | 'error' | 'success';
  /** font awesome icon */
  icon?: ReactNode;
  /** duration in ms。 0 means no auto close */
  duration?: number;
  closeable?: boolean;
  onClose?: (id: string) => void;
}

function SCToastContainer({ messageMap }: { messageMap: Record<string, SCToastProps> }) {
  return (
    <div className="sce-toast-container">
      {Object.keys(messageMap).map((id) => (
        <SCToast key={id} {...messageMap[id]} />
      ))}
    </div>
  );
}

const messageKey = 'sc_message';
// let messageId = 0;
let messageMap: Record<string, SCToastProps> = {};

const SCToast = ({
  id,
  content,
  type,
  duration = 2000,
  closeable = true,
  onClose,
}: SCToastProps) => {
  const isMounted = useMountedState();

  useEffect(() => {
    if (!duration) {
      return;
    }

    const timer = setTimeout(() => {
      if (isMounted()) {
        onClose?.(id);
      }
    }, duration);

    return () => {
      clearTimeout(timer);
    };
  }, [duration]);

  const typeIcon =
    type === 'info'
      ? faInfoCircle
      : type === 'warning'
        ? faExclamationTriangle
        : type === 'error'
          ? faBan
          : type === 'success'
            ? faCheckCircle
            : null;

  return (
    <div
      className={classNames('sce-toast', {
        [`sce-toast-type-${type}`]: type,
      })}
    >
      {typeIcon && <FontAwesomeIcon icon={typeIcon} />}
      <div className="sce-toast-content">{content}</div>
      {closeable && (
        <FontAwesomeIcon icon={faXmark} className="sce-toast-close" onClick={() => onClose?.(id)} />
      )}
    </div>
  );
};

SCToast.show = ({ ...rest }: Omit<SCToastProps, 'id'>) => {
  const id = `sc_message_${rest.content}`;

  const text = getTextContent(rest.content);
  console.info('SCToast.show', rest.type, JSON.stringify(text), {
    id,
    ...rest,
  });

  messageMap[id] = {
    ...rest,
    id,
    onClose: (id) => {
      SCToast.destroy(id);
    },
  };

  SCComponentRoot.renderComponent(messageKey, <SCToastContainer messageMap={messageMap} />);

  function update(props: Omit<SCToastProps, 'id'>) {
    messageMap[id] = {
      ...messageMap[id],
      ...props,
      id,
    };

    SCComponentRoot.renderComponent(messageKey, <SCToastContainer messageMap={messageMap} />);
  }

  function destroy() {
    SCToast.destroy(id);
  }

  return {
    update,
    destroy,
  };
};

function getTextContent(unknown: unknown) {
  if (React.isValidElement(unknown)) {
    const node = unknown as React.ReactElement;
    return node.props.children
      ? React.Children.map(node.props.children, getTextContent).join(' ')
      : '';
  }
  return String(unknown);
}

SCToast.info = ({ ...rest }: Omit<SCToastProps, 'id'>) => {
  return SCToast.show({ ...rest, type: 'info' });
};

SCToast.warning = ({ ...rest }: Omit<SCToastProps, 'id'>) => {
  return SCToast.show({ ...rest, type: 'warning' });
};

SCToast.error = ({ ...rest }: Omit<SCToastProps, 'id'>) => {
  return SCToast.show({ ...rest, type: 'error' });
};

SCToast.success = ({ ...rest }: Omit<SCToastProps, 'id'>) => {
  return SCToast.show({ ...rest, type: 'success' });
};

SCToast.destroy = (id?: string) => {
  if (id) {
    delete messageMap[id];
  } else {
    messageMap = {};
  }

  if (Object.keys(messageMap).length === 0) {
    // remove
    SCComponentRoot.renderComponent(messageKey, null);
  } else {
    // update
    SCComponentRoot.renderComponent(messageKey, <SCToastContainer messageMap={messageMap} />);
  }
};

export { SCToast };
export type { SCToastProps };
